import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { deriveDisplayName } from '../../contexts/AuthContext';
import {
  fetchAdminUserChatHistory,
  fetchAdminUserChatSession,
} from '../../services/authService';

function formatSessionDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMessageTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function messageContentText(content) {
  if (typeof content === 'string') return content;
  if (content && typeof content === 'object') {
    if (content.type === 'document') {
      const name = content.filename || 'Document';
      return `[document] ${name}`;
    }
    return String(content.text || content.content || '').trim();
  }
  return '';
}

function sessionTitle(session) {
  return session?.tag || session?.title || 'Untitled chat';
}

function sessionInitial(title) {
  const first = String(title || '').trim();
  return first ? first[0].toUpperCase() : 'C';
}

function HistoryState({ children, compact = false }) {
  return (
    <div className={`user-chat-history-state${compact ? ' user-chat-history-state--compact' : ''}`}>
      {children}
    </div>
  );
}

export default function UserChatHistoryModal({ open, user, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const messagesContainerRef = useRef(null);

  const displayName = user?.full_name || deriveDisplayName(user?.email);

  useEffect(() => {
    if (!open || !user?.user_id) return undefined;
    let cancelled = false;
    setLoadingSessions(true);
    setError('');
    setSessions([]);
    setSelectedSessionId(null);
    setSessionDetail(null);
    setSearch('');

    fetchAdminUserChatHistory(user.user_id)
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setSessions(list);
        if (list.length > 0) {
          const firstId = list[0]?.session_id ?? list[0]?.sessionId;
          if (firstId) setSelectedSessionId(firstId);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not load chat history.');
      })
      .finally(() => {
        if (!cancelled) setLoadingSessions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, user?.user_id]);

  useEffect(() => {
    if (!open || !user?.user_id || !selectedSessionId) {
      setSessionDetail(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingMessages(true);
    fetchAdminUserChatSession(user.user_id, selectedSessionId)
      .then((data) => {
        if (!cancelled) setSessionDetail(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setSessionDetail(null);
          setError(err?.message || 'Could not load this conversation.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMessages(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, user?.user_id, selectedSessionId]);

  useEffect(() => {
    if (!loadingMessages && sessionDetail?.messages?.length) {
      messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [loadingMessages, sessionDetail, selectedSessionId]);

  const filteredSessions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((session) => {
      const title = sessionTitle(session).toLowerCase();
      const platform = String(session?.platform || '').toLowerCase();
      const sid = String(session?.session_id ?? session?.sessionId ?? '').toLowerCase();
      return title.includes(q) || platform.includes(q) || sid.includes(q);
    });
  }, [sessions, search]);

  const selectedSession = useMemo(
    () =>
      sessions.find(
        (session) => (session?.session_id ?? session?.sessionId) === selectedSessionId
      ) || null,
    [sessions, selectedSessionId]
  );

  const messages = Array.isArray(sessionDetail?.messages) ? sessionDetail.messages : [];

  if (!open || !user || typeof document === 'undefined') return null;

  return createPortal(
    <div className="user-chat-history-overlay" onClick={onClose} role="presentation">
      <div
        className="user-chat-history-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-chat-history-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="user-chat-history-topbar">
          <div className="user-chat-history-user">
            <span className="user-chat-history-user-avatar" aria-hidden="true">
              {sessionInitial(displayName)}
            </span>
            <div>
              <span className="user-chat-history-kicker">Chat History</span>
              <h2 id="user-chat-history-title">{displayName}</h2>
              <p>{user.email}</p>
            </div>
          </div>
          <button type="button" className="user-chat-history-close" onClick={onClose} aria-label="Close chat history">
            ×
          </button>
        </header>

        {error ? <div className="user-chat-history-banner">{error}</div> : null}

        <div className="user-chat-history-body">
          <aside className="user-chat-history-sidebar">
            <div className="user-chat-history-sidebar-head">
              <h3>Conversations</h3>
              <span>{sessions.length}</span>
            </div>
            <label className="user-chat-history-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sessions..."
              />
            </label>
            <div className="user-chat-history-session-list">
              {loadingSessions ? (
                <HistoryState compact>Loading conversations…</HistoryState>
              ) : filteredSessions.length ? (
                filteredSessions.map((session) => {
                  const sid = session.session_id ?? session.sessionId;
                  const title = sessionTitle(session);
                  const active = sid === selectedSessionId;
                  return (
                    <button
                      key={sid}
                      type="button"
                      className={`user-chat-history-session${active ? ' is-active' : ''}`}
                      onClick={() => setSelectedSessionId(sid)}
                    >
                      <span className="user-chat-history-session-avatar" aria-hidden="true">
                        {sessionInitial(title)}
                      </span>
                      <span className="user-chat-history-session-copy">
                        <strong>{title}</strong>
                        {session.platform ? (
                          <span className="user-chat-history-session-platform">{session.platform}</span>
                        ) : null}
                        <span className="user-chat-history-session-date">
                          {formatSessionDate(session.created_at ?? session.createdAt)}
                        </span>
                      </span>
                      {active ? <span className="user-chat-history-session-chevron" aria-hidden="true">›</span> : null}
                    </button>
                  );
                })
              ) : (
                <HistoryState compact>No conversations found for this user.</HistoryState>
              )}
            </div>
          </aside>

          <section className="user-chat-history-main">
            {selectedSession ? (
              <>
                <div className="user-chat-history-main-head">
                  <span className="user-chat-history-session-avatar user-chat-history-session-avatar--large" aria-hidden="true">
                    {sessionInitial(sessionTitle(selectedSession))}
                  </span>
                  <div className="user-chat-history-main-head-copy">
                    <strong>{sessionTitle(selectedSession)}</strong>
                    {selectedSession.platform ? (
                      <span className="user-chat-history-main-platform">{selectedSession.platform}</span>
                    ) : null}
                    <span className="user-chat-history-main-meta">
                      Last active · {formatSessionDate(selectedSession.created_at ?? selectedSession.createdAt)}
                    </span>
                  </div>
                </div>
                <div className="user-chat-history-messages" ref={messagesContainerRef}>
                  {loadingMessages ? (
                    <HistoryState>Loading messages…</HistoryState>
                  ) : messages.length ? (
                    <>
                      {messages.map((message, index) => {
                        const isUser = message.role === 'user';
                        const text = messageContentText(message.content);
                        if (!text) return null;
                        const time = formatMessageTime(message.timestamp || message.createdAt);
                        return (
                          <div
                            key={message.id || `msg-${index}`}
                            className={`user-chat-history-message${isUser ? ' is-user' : ' is-assistant'}`}
                          >
                            {!isUser ? (
                              <span className="user-chat-history-message-avatar" aria-hidden="true">
                                {sessionInitial(displayName)}
                              </span>
                            ) : null}
                            <div className="user-chat-history-message-stack">
                              {!isUser ? (
                                <span className="user-chat-history-message-sender">{displayName}</span>
                              ) : null}
                              <div className="user-chat-history-message-bubble">
                                {isUser ? (
                                  <p>{text}</p>
                                ) : (
                                  <div className="user-chat-history-markdown">
                                    <Markdown remarkPlugins={[remarkGfm]}>{text}</Markdown>
                                  </div>
                                )}
                                {time ? <span className="user-chat-history-message-time">{time}</span> : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <HistoryState>No messages in this conversation yet.</HistoryState>
                  )}
                </div>
              </>
            ) : (
              <HistoryState>Select a conversation from the list to view messages.</HistoryState>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}
